import React, { useState, useEffect, useCallback } from 'react';
import './Content.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Content = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', content: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const fetchPages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/pages`);
      const data = await res.json();
      setPages(data);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleEdit = (page) => {
    setEditing(page.id);
    setForm({ title: page.title, slug: page.slug, content: page.content || '', is_active: page.is_active });
    setShowAdd(false);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm({ title: '', slug: '', content: '', is_active: true });
    setShowAdd(true);
  };

  const handleCancel = () => {
    setEditing(null);
    setShowAdd(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) return alert('Title and slug are required');
    setSaving(true);
    try {
      const url = editing ? `${API_URL}/pages/${editing}` : `${API_URL}/pages`;
      const method = editing ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      await fetchPages();
      setEditing(null);
      setShowAdd(false);
    } catch (err) {
      console.error('Failed to save page:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (page) => {
    try {
      await fetch(`${API_URL}/pages/${page.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...page, is_active: !page.is_active }),
      });
      await fetchPages();
    } catch (err) {
      console.error('Failed to toggle page:', err);
    }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/pages/${page.id}`, { method: 'DELETE' });
      await fetchPages();
    } catch (err) {
      console.error('Failed to delete page:', err);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  if (loading) return <div className="content-loading">Loading pages...</div>;

  return (
    <div className="content-management">
      <div className="content-header">
        <div>
          <h2>Content Management</h2>
          <p className="content-subtitle">Manage your website pages — About, FAQ, Policies, and more</p>
        </div>
        <button className="btn-add-page" onClick={handleAdd}>+ Add Page</button>
      </div>

      {(editing || showAdd) && (
        <div className="content-editor">
          <h3>{editing ? 'Edit Page' : 'Add New Page'}</h3>
          <div className="editor-fields">
            <div className="field-row">
              <div className="field-group">
                <label>Page Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setForm(f => ({
                      ...f,
                      title,
                      slug: !editing ? generateSlug(title) : f.slug
                    }));
                  }}
                  placeholder="e.g. About Us"
                />
              </div>
              <div className="field-group">
                <label>URL Slug</label>
                <div className="slug-preview">
                  <span className="slug-prefix">/</span>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder="e.g. about"
                  />
                </div>
              </div>
            </div>
            <div className="field-group">
              <label>Content (HTML)</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Enter page content in HTML..."
                rows={16}
              />
            </div>
            <div className="field-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                />
                Page is active (visible on site)
              </label>
            </div>
          </div>
          <div className="editor-actions">
            <button className="btn-save" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Page'}
            </button>
            <button className="btn-cancel" onClick={handleCancel}>Cancel</button>
            {form.content && (
              <button className="btn-preview" onClick={() => {
                const w = window.open('', '_blank');
                w.document.write(`<html><head><title>${form.title}</title>
                  <style>body{font-family:system-ui,sans-serif;max-width:800px;margin:40px auto;padding:0 20px;color:#333;line-height:1.6}
                  h2{color:#1A1A1A}h3{color:#333;margin-top:24px}ul,ol{padding-left:24px}li{margin-bottom:8px}</style>
                  </head><body>${form.content}</body></html>`);
                w.document.close();
              }}>Preview</button>
            )}
          </div>
        </div>
      )}

      <div className="pages-table">
        <table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(page => (
              <tr key={page.id} className={!page.is_active ? 'inactive-row' : ''}>
                <td className="page-title-cell">{page.title}</td>
                <td className="page-slug-cell">
                  <a href={`/${page.slug}`} target="_blank" rel="noopener noreferrer">/{page.slug}</a>
                </td>
                <td>
                  <span className={`status-badge ${page.is_active ? 'active' : 'inactive'}`}>
                    {page.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="page-date-cell">
                  {page.updated_at ? new Date(page.updated_at).toLocaleDateString() : '—'}
                </td>
                <td className="page-actions-cell">
                  <button className="btn-edit" onClick={() => handleEdit(page)}>Edit</button>
                  <button className="btn-toggle" onClick={() => handleToggle(page)}>
                    {page.is_active ? 'Hide' : 'Show'}
                  </button>
                  <button className="btn-delete" onClick={() => handleDelete(page)}>Delete</button>
                </td>
              </tr>
            ))}
            {pages.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No pages yet. Click "Add Page" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Content;
